// src/pages/shops/ShopsPage.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Store, Plus, Edit, Trash2, MoreVertical, Package, Truck,
  MapPin, Phone, Hash, AlertCircle, RefreshCw, Image, Users, ShoppingBag,
} from "lucide-react";
import { PageHeader, StatsCards, SearchBar, PageControls, DataTable } from "../../components/common";
import { getShops, deleteShop } from "../../services/api/shops";
import { usePermissions } from "../../hooks/usePermissions";
import { useAuth } from "../../hooks/useAuth";
import { useLocalization } from "../../contexts/LocalizationContext";
import { getImageUrl, handleImageError } from "../../utils/imageUtils";
import styles from "./ShopsPage.module.css";

const ShopsPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const { t } = useLocalization();

  const [shops, setShops] = useState([]);
  const [filteredShops, setFilteredShops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [activeDropdown, setActiveDropdown] = useState(null);

  const shopTypes = {
    online: { label: t("shops.online"), icon: Store, color: "#7F5CFF" },
    pos: { label: t("shops.pos"), icon: Package, color: "#f59e0b" },
    dropshipper: { label: t("shops.dropshipper"), icon: Truck, color: "#10b981" },
  };

  useEffect(() => { fetchShops(); }, []);
  useEffect(() => { filterShops(); }, [shops, searchQuery, typeFilter]);

  const fetchShops = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getShops();
      setShops(response);
    } catch (error) {
      console.error("Failed to fetch shops:", error);
      setError(t("errors.somethingWentWrong"));
    } finally {
      setIsLoading(false);
    }
  };

  const filterShops = () => {
    let filtered = shops;
    if (typeFilter !== "all") filtered = filtered.filter((s) => s.type === typeFilter);
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (shop) =>
          shop.name.toLowerCase().includes(query) ||
          shop.orderNumberPrefix.toLowerCase().includes(query) ||
          (shop.address && shop.address.toLowerCase().includes(query)) ||
          (shop.phone && shop.phone.includes(query))
      );
    }
    setFilteredShops(filtered);
  };

  const handleDeleteShop = async (shopId, shopName) => {
    if (!window.confirm(t("shops.confirmDelete", { shopName }))) return;
    try {
      await deleteShop(shopId);
      setShops((prev) => prev.filter((shop) => shop.id !== shopId));
      alert(t("shops.shopDeletedSuccess", { shopName }));
    } catch (error) {
      console.error("Failed to delete shop:", error);
      alert(t("shops.failedToDelete"));
    }
    setActiveDropdown(null);
  };

  const toggleDropdown = (shopId) => {
    setActiveDropdown(activeDropdown === shopId ? null : shopId);
  };

  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getShopTypeStats = () => ({
    all: shops.length,
    online: shops.filter((s) => s.type === "online").length,
    pos: shops.filter((s) => s.type === "pos").length,
    dropshipper: shops.filter((s) => s.type === "dropshipper").length,
  });

  const getStatsConfig = () => {
    const stats = getShopTypeStats();
    return [
      { key: "all", label: t("shops.allShops"), value: stats.all, active: typeFilter === "all", onClick: () => setTypeFilter("all") },
      ...Object.entries(shopTypes).map(([type, config]) => ({
        key: type, label: config.label, value: stats[type],
        icon: config.icon, color: config.color, bgColor: `${config.color}15`,
        active: typeFilter === type, onClick: () => setTypeFilter(type),
      })),
    ];
  };

  const columns = [
    {
      key: "logo", header: t("shops.logo"), width: "80px",
      render: (shop) => (
        <div className={styles.shopLogo}>
          {shop.logo ? (
            <img src={getImageUrl(shop.logo)} alt={`${shop.name} logo`} className={styles.logoImage} onError={(e) => handleImageError(e)} />
          ) : (
            <div className={styles.logoPlaceholder}><Image size={16} /></div>
          )}
        </div>
      ),
    },
    {
      key: "name", header: t("shops.shopName"), width: "30%",
      render: (shop) => (
        <div className={styles.shopInfo}>
          <span className={styles.shopName}>{shop.name}</span>
          {shop.address && <span className={styles.shopAddress}><MapPin size={12} />{shop.address}</span>}
        </div>
      ),
    },
    {
      key: "type", header: t("shops.type"), width: "15%",
      render: (shop) => {
        const typeConfig = shopTypes[shop.type];
        const TypeIcon = typeConfig?.icon || Store;
        const isResellerShop = shop.resellerBusinessId === user?.businessId && shop.businessId !== user?.businessId;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div className={styles.shopTypeBadge} style={{ backgroundColor: `${typeConfig?.color}15`, color: typeConfig?.color }}>
              <TypeIcon size={14} /><span>{typeConfig?.label}</span>
            </div>
            {isResellerShop && (
              <div className={styles.shopTypeBadge} style={{ backgroundColor: '#6b728015', color: '#6b7280', fontSize: '11px' }}>
                <span>Provider Shop</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "orderNumberPrefix", header: t("shops.prefix"), width: "12%", mobileHidden: true,
      render: (shop) => (
        <div className={styles.prefixBadge}><Hash size={12} /><span>{shop.orderNumberPrefix}</span></div>
      ),
    },
    {
      key: "phone", header: t("shops.contact"), width: "20%", mobileHidden: true,
      render: (shop) =>
        shop.phone ? (
          <div className={styles.contactInfo}><Phone size={12} /><span>{shop.phone}</span></div>
        ) : (
          <span className={styles.noContact}>{t("shops.noContact")}</span>
        ),
    },
  ];

  const renderActions = (shop) => {
    const isResellerShop = shop.resellerBusinessId === user?.businessId && shop.businessId !== user?.businessId;
    return (
    <div className={styles.actionsWrapper}>
      {hasPermission("assign_products_to_shop") && !isResellerShop && (
        <Link
          to={`/shops/${shop.id}/assign-products`}
          className={`${styles.actionButton} ${styles.assignProductsButton}`}
          title="Assign Products"
        >
          <ShoppingBag size={14} />
        </Link>
      )}

      {hasPermission("assign_users_to_shop") && (
        <Link
          to={`/shops/${shop.id}/assign-users`}
          className={`${styles.actionButton} ${styles.assignUsersButton}`}
          title="Assign Users"
        >
          <Users size={14} />
        </Link>
      )}

      {hasPermission("edit_shop") && (
        <Link
          to={`/shops/${shop.id}/edit`}
          className={`${styles.actionButton} ${styles.editButton}`}
          title={t("common.edit")}
        >
          <Edit size={14} />
        </Link>
      )}

      {(hasPermission("edit_shop") || hasPermission("delete_shop")) && (
        <div className={styles.dropdownWrapper}>
          <button
            className={`${styles.actionButton} ${styles.moreButton}`}
            onClick={(e) => { e.stopPropagation(); toggleDropdown(shop.id); }}
            title={t("common.moreActions")}
          >
            <MoreVertical size={14} />
          </button>
          {activeDropdown === shop.id && (
            <div className={styles.dropdownMenu}>
              {hasPermission("assign_products_to_shop") && !isResellerShop && (
                <Link to={`/shops/${shop.id}/assign-products`} className={styles.dropdownItem}>
                  <ShoppingBag size={14} />Assign Products
                </Link>
              )}
              {hasPermission("assign_users_to_shop") && (
                <Link to={`/shops/${shop.id}/assign-users`} className={styles.dropdownItem}>
                  <Users size={14} />Assign Users
                </Link>
              )}
              {hasPermission("edit_shop") && (
                <Link to={`/shops/${shop.id}/edit`} className={styles.dropdownItem}>
                  <Edit size={14} />{t("common.edit")}
                </Link>
              )}
              {hasPermission("delete_shop") && (
                <button className={`${styles.dropdownItem} ${styles.deleteItem}`} onClick={() => handleDeleteShop(shop.id, shop.name)}>
                  <Trash2 size={14} />{t("common.delete")}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
  };

  if (isLoading) {
    return (
      <div className={styles.shopsPage}>
        <PageHeader title={t("sidebar.allShops")} description={t("shops.manageYourShops")} icon={Store} />
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <h3>{t("shops.loadingShops")}</h3>
          <p>{t("shops.loadingShopsDesc")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.shopsPage}>
        <PageHeader title={t("sidebar.allShops")} description={t("shops.manageYourShops")} icon={Store} />
        <div className={styles.errorState}>
          <AlertCircle className={styles.errorIcon} size={48} />
          <h3>{t("errors.somethingWentWrong")}</h3>
          <p>{error}</p>
          <button onClick={fetchShops} className={styles.retryButton}><RefreshCw size={16} />{t("common.retry")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.shopsPage}>
      <PageHeader
        title={t("sidebar.allShops")}
        description={t("shops.manageYourShops")}
        icon={Store}
        actions={
          hasPermission("create_shop") && (
            <Link to="/shops/create" className={styles.primaryButton}>
              <Plus size={20} />{t("sidebar.addShop")}
            </Link>
          )
        }
      />
      <StatsCards stats={getStatsConfig()} minColumnWidth="180px" clickable />
      <PageControls
        left={<SearchBar value={searchQuery} onChange={setSearchQuery} placeholder={t("shops.searchShops")} autoSearch debounceMs={300} />}
        right={
          <div className={styles.resultsInfo}>
            {searchQuery || typeFilter !== "all" ? (
              <span>{t("shops.filteredResults", { count: filteredShops.length, total: shops.length })}</span>
            ) : (
              <span>{t("shops.totalShops", { count: shops.length })}</span>
            )}
          </div>
        }
      />
      <DataTable
        data={filteredShops}
        columns={columns}
        actions={{ render: renderActions, width: "180px", frozen: true }}
        loading={false}
        emptyMessage={searchQuery || typeFilter !== "all" ? t("shops.noSearchResultsDesc") : t("shops.noShopsDesc")}
        emptyIcon={
          searchQuery || typeFilter !== "all"
            ? <AlertCircle size={48} style={{ color: "#9ca3af" }} />
            : <Store size={48} style={{ color: "#9ca3af" }} />
        }
        minWidth="900px"
      />
    </div>
  );
};

export default ShopsPage;